import graphql__4fc053508474c0b3347f from "./__generated__/SuggestionCardContentQuery.graphql.js";import { useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import ContributionHeader from "../Contributions/ContributionHeader";
import RichText from "../Utils/RichText";
import { useBottomSheetPanelContext } from "../Panels/BottomSheetPanel";
import SuggestionCardActions from "./SuggestionCardActions";

// Triggers the Relay query for a single contribution and renders its content
// and author info in the scrollable area. Vote buttons, the comment trigger
// and the comment form/published card are injected into the sheet's sticky
// actions footer slot so they remain visible regardless of scroll position.
// Must be wrapped in a Suspense boundary by the parent component.
//
const SuggestionCardContent = ({ contributionId, onCommentFormOpenChange }) => {
  const { snapToContent, setActionsFooterContent } = useBottomSheetPanelContext();

  // Re-snap the bottom sheet to fit this card's content each time the
  // contribution changes, once the Relay query resolves and the new content
  // renders.
  useEffect(() => {
    if (snapToContent) snapToContent();
  }, [contributionId, snapToContent]);

  const data = useLazyLoadQuery(
    SuggestionCardContentQuery,
    { contributionId },
    { fetchPolicy: "store-or-network" }
  );

  const contribution = data.contribution;

  // Inject the action row (votes + comment trigger) and the comment body into
  // the sheet's sticky actions footer slot. The footer renders outside the
  // scroll container so these controls are always reachable.
  //
  // Note: Relay fragment refs (contribution) are stable identifiers — passing
  // them as props into footer JSX is safe because `useFragment` always reads
  // current data from the live Relay store at render time.
  //
  // The cleanup clears the footer on card transitions and on unmount. React 18
  // batches the cleanup's null write with the next effect body, so there is no
  // visible null flash when navigating from one card to the next.
  useEffect(() => {
    if (!contribution) {
      setActionsFooterContent(null);
      return;
    }

    setActionsFooterContent(
      <SuggestionCardActions
        key={contribution.id} // Reset internal state when switching contributions
        contribution={contribution}
        onCommentFormOpenChange={onCommentFormOpenChange}
      />
    );

    return () => setActionsFooterContent(null);
  }, [contribution, setActionsFooterContent, onCommentFormOpenChange]);

  if (!contribution) return null;

  const { title, content } = contribution;

  return (
    <>
      <ContributionHeader contribution={contribution} />

      {title && <h3 className="text-title-section margin-bottom-sm">{title}</h3>}

      <RichText className="description" content={content} />
    </>
  );
};

export default SuggestionCardContent;

const SuggestionCardContentQuery = graphql__4fc053508474c0b3347f;
